// Shared error narrowing so call sites can use `catch (err: unknown)` instead
// of `catch (err: any)`. Mirrors the axios/FastAPI error shape this app sees:
// err.response.data.detail (FastAPI HTTPException) then err.message.

export interface ApiErrorShape {
  response?: { status?: number; data?: { detail?: unknown } };
  message?: unknown;
}

/** Best-effort human-readable message from an unknown thrown value. */
export function getErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string' && detail) return detail;
    // FastAPI validation errors return detail as an array of { msg } items.
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
        .filter(Boolean);
      if (msgs.length) return msgs.join(', ');
    }
    if (typeof e.message === 'string' && e.message) return e.message;
  }
  return fallback;
}

/** HTTP status code from an unknown thrown value, if it carries one. */
export function getErrorStatus(err: unknown): number | undefined {
  if (err && typeof err === 'object') {
    const status = (err as ApiErrorShape).response?.status;
    if (typeof status === 'number') return status;
  }
  return undefined;
}
